import { DisclosurePlaceholdersResponseModel } from '@/customApiClients/mgmtApiClient/Api';

import { MarkdownContent } from '@/components/ui/markdown-content';
import { Separator } from '@/components/ui/separator';

import { useDisclosureSettings } from '@/hooks/useDisclosureSettings';

const PLACEHOLDER_HINTS: Record<
    keyof DisclosurePlaceholdersResponseModel,
    string
> = {
    solutionName: '[client name / project name / solution name]',
    solutionPurpose:
        '[high-level purpose, e.g. data exploration, reporting support, insight generation, performance monitoring, decision support]',
    solutionDataSources:
        '[selected client-specific structured data sources / relevant business data domains]',
    solutionUserGroups: '[primary user groups]'
};

const formatLastEdited = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });

const DISCLOSURE_BODY = `
## 2. What is this tool?

This Chat Agent is an AI-powered interface that allows users to query, explore,
and analyze data from connected client datasets. Rather than navigating complex
reporting tools or waiting for scheduled outputs, users can ask questions in
plain language and receive answers in the form of summaries, tables, charts, or
narrative analysis — on demand and at their own pace.

The agent combines the reasoning capabilities of a large language model with
direct access to structured client data. It is intended solely as an analytical
assistant within the scope of the specific project or dataset it has been
configured for. It is not a general-purpose research tool, and its responses
should always be understood in that context.

## 3. Closed system and data handling

The agents run on a closed, access-controlled deployment. Three properties follow
from this:

**No external retrieval.** The agents have no internet browsing or external
search capability. Responses are grounded exclusively in the connected client
datasets through retrieval-augmented generation. The agents cannot reach public
websites, public chat services or any source outside the configured scope.

**No training on client data.** Prompts, queries and connected data are not used
to train or fine-tune any underlying model. The model is used for inference only.
Inputs and outputs are automatically deleted from the model provider's systems
within 30 days.

**Tenant isolation.** Data is held in a dedicated client environment. Access is
governed by defined user groups, and users can only query the agents and datasets
they have been granted access to.

This is distinct from publicly available consumer AI services, which may retrieve
information from the open internet and may use conversations for model
improvement.

## 4. Intended uses

This tool is designed to support a range of analytical activities, from
structured reporting to open-ended exploration. Key use cases include:

**Ad-hoc data exploration.** Users can pose questions that fall outside of
standard reports, drilling into specific segments, time periods, or combinations
of variables that pre-built dashboards may not cover.

**Exploratory analysis and discovery.** One of the most valuable applications of
this tool is enabling users to investigate the data without a fixed hypothesis.
By asking broad or comparative questions, users can surface patterns, anomalies,
and relationships that might not be visible through conventional reporting. This
kind of exploratory use is actively encouraged, as it often leads to the most
meaningful insights.

**Structured analysis.** The agent can produce breakdowns and comparisons across
dimensions such as time, geography, category, or audience segment, giving users a
more granular view of performance or behavior.

**Insight generation.** Beyond presenting numbers, the agent can help
contextualize findings, highlight what stands out, and frame results in a way
that supports interpretation and discussion.

**Flexible output formats.** Results can be delivered as plain-language
summaries, detailed analyses, structured tables, or visualizations, depending on
what is most useful for the task at hand.

The tool is not intended for use outside the scope of the connected client data,
for strategic decisions without human validation, or as a replacement for formal
reporting, expert judgment, or approved data governance processes.

## 5. Five things to keep in mind when using the Chat Agent

### 1. AI can make mistakes

The agent is a powerful tool, but it is not infallible. It may return incorrect
figures, misinterpret a question, apply the wrong filter, or draw an incomplete
conclusion, particularly when queries are complex or ambiguous. The data
retrieval and calculation logic is generated automatically and may contain errors
that are not immediately obvious from the output alone.

For this reason, the agent's output is best treated as a starting point rather
than a final source of truth. Metrics or findings that will inform significant
decisions, be shared with clients, or appear in formal deliverables should be
validated against primary reports or other established data sources before use.
The agent is well-suited to exploration and initial analysis; critical numbers
should always be subject to an independent check.

### 2. Provide as much context as possible

The agent has no persistent memory between sessions and no inherent knowledge of
your specific project, objectives, or organizational context. Each conversation
begins from scratch. If you assume the agent already understands your team's
priorities, the meaning of internal terminology, or the nuances of your dataset,
responses are likely to be less accurate and less useful than they could be.

It helps to treat the agent as a capable analyst who is new to your project.
Useful context includes the time you are focused on, the audience or market
segment you care about, how particular terms or categories are used in your data,
and what decision or output your question is meant to support.

### 3. Be clear and precise, and iterate when needed

Vague or open-ended questions will lead the agent to make assumptions, and those
assumptions may not align with your intent. The agent will generally do its best
to interpret an ambiguous request, but its interpretation may not match what you
had in mind.

Refining a question across multiple turns often yields significantly better
results than a single broad prompt. Specifying the exact metric, time range,
dimension, or comparison you are interested in gives the agent much more to work
with.

### 4. Specify the output you want

The agent can present information in many ways, including a brief plain-language
summary, a detailed analytical breakdown, a structured table, or a visualization.
Without guidance, it will make a judgment call about format, which may or may not
suit your needs or the audience you are preparing content for.

Being explicit about the format you need saves time and ensures the output is
immediately usable. Prompts such as "give me a short summary I can use in a
presentation," "show me this as a table with percentage breakdowns," or "produce
a bar chart sorted by volume" give the agent clear direction and tend to produce
much more actionable results.

### 5. Share feedback

The chat agent is a continuously evolving tool. Its quality, both in terms of how
well it interprets questions and how accurately it analyses the underlying data,
improves over time through structured feedback from users. Passive use without
feedback limits that improvement cycle.

Using the thumbs up/down rating feature and adding a brief comment where possible
— whether to flag an inaccurate result, an off-topic response, or a particularly
useful output — helps shape the development of the tool. Feedback does not need
to be detailed to be valuable, and every signal contributes to making the agent
more reliable and useful for the whole team over time.

## 6. Limitations

This agent is configured for use within a specific client or project context.
While the underlying language model has broad general capabilities, responses are
grounded in and should be evaluated in relation to the connected client data. It
should not be relied upon for factual claims about the world beyond that dataset,
for legal or financial advice, or for conclusions that require human expertise,
ethical judgment, or organizational authority.

When in doubt about whether a question falls within the intended scope of this
tool, err on the side of consulting a human expert or validated report.
`;

type PlaceholderKey = keyof DisclosurePlaceholdersResponseModel;

function TemplateField({
    field,
    placeholders
}: {
    field: PlaceholderKey;
    placeholders: DisclosurePlaceholdersResponseModel;
}) {
    const value = placeholders[field]?.trim();
    if (value) return <>{value}</>;

    return (
        <mark
            data-template-field
            className="bg-destructive/10 px-1 py-0.5 rounded-sm font-medium text-destructive"
        >
            {PLACEHOLDER_HINTS[field]}
        </mark>
    );
}

function CustomerUseCaseSection({
    placeholders
}: {
    placeholders: DisclosurePlaceholdersResponseModel;
}) {
    return (
        <section className="flex flex-col gap-2 text-secondary-foreground text-sm leading-relaxed">
            <h2 className="font-medium text-foreground text-base">
                1. Customer use case and scope
            </h2>

            <p>
                This disclosure describes the intended use of the AI-powered
                chat agent in the context of{' '}
                <TemplateField
                    field="solutionName"
                    placeholders={placeholders}
                />
                .
            </p>

            <p>
                At the time of writing, the solution is introduced to support{' '}
                <TemplateField
                    field="solutionPurpose"
                    placeholders={placeholders}
                />{' '}
                based on the following data sources:{' '}
                <TemplateField
                    field="solutionDataSources"
                    placeholders={placeholders}
                />
                .
            </p>

            <p>
                The solution is intended to help{' '}
                <TemplateField
                    field="solutionUserGroups"
                    placeholders={placeholders}
                />{' '}
                interact more easily with relevant data through natural language
                queries and receive output such as summaries, tables, charts,
                and analytical explanations.
            </p>

            <p>
                The solution is configured for a defined client or project
                context and should be understood as an assistive analytical tool
                within that context. Depending on future needs, the scope may be
                extended to include additional datasets, business domains, use
                cases, or user groups. Where this happens, the overall purpose
                remains the same: to support more accessible, efficient, and
                informed use of connected client data through an AI-assisted
                interface.
            </p>

            <p>
                This tool is not intended to replace formal reporting, approved
                governance processes, expert judgment, or independent
                validation, and should not be used without appropriate human
                oversight.
            </p>
        </section>
    );
}

export function DisclosurePanel() {
    const { placeholders, updatedAt } = useDisclosureSettings();

    return (
        <article className="flex flex-col gap-4 mx-auto max-w-prose">
            <header className="flex flex-col gap-1.5">
                <h1 className="font-medium text-foreground text-xl leading-tight">
                    Intended Uses Transparency Disclosure
                </h1>
                <p className="text-muted-foreground text-sm">
                    AI-Powered Chat Agent for Data Analysis and Insights
                    {updatedAt && (
                        <>
                            <span aria-hidden className="mx-1.5">
                                -
                            </span>
                            <time dateTime={updatedAt}>
                                Last edited {formatLastEdited(updatedAt)}
                            </time>
                        </>
                    )}
                </p>
            </header>

            <Separator />

            <CustomerUseCaseSection placeholders={placeholders} />

            <MarkdownContent className="text-secondary-foreground leading-relaxed">
                {DISCLOSURE_BODY}
            </MarkdownContent>

            <Separator />

            <p className="text-muted-foreground text-xs italic">
                This disclosure is intended to promote informed, responsible,
                and effective use of AI-assisted data analysis. It should be
                reviewed periodically as the tool evolves.
            </p>
        </article>
    );
}
